import { c as _c } from "react/compiler-runtime";
import { DateRangePicker, Hint, Tooltip, getDate } from '@systemeio/ui';
import { actionTitleLocoKeys } from 'modules/automation-rules/actions/constants/action-loco-keys';
import { useEffect } from 'react';
import ListingLayout from 'shared/components/listing-layout';
import { Table } from 'shared/components/table/table';
import { ColumnDefinitionInterface } from 'shared/components/table/types/column-interface';
import { withErrorBoundary } from 'shared/hocs/with-error-boundary';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import useUser from 'shared/hooks/use-user';
import { useFilterPagination } from 'shared/route-query/hooks/use-filter-pagination';
import { OrderEnum } from 'shared/types/pagination-interface';
import { dateFormat, dateTimeFormat } from 'shared/utils/date-time-format';
import { getDateRangeSinceDate, getToday } from 'shared/utils/get-dates';
import { ContactId, ContactInterface } from '../../types/contact-interface';
import { useContactAutomationTasks } from '../hooks/use-contact-automation-tasks';
import { ContactAutomationTaskInterface } from '../types/contact-automation-task-interface';
import { ContactPageFilterInterface } from '../types/contact-page-filter-interface';
import ContactAutomationTaskLink from './contact-automation-task-link';
interface ContactAutomationTasksProps {
  contactId: ContactId;
  contact?: ContactInterface;
}
const defaultFilters: ContactPageFilterInterface = {
  date: [null, null]
};
const ContactAutomationTasks = t0 => {
  const $ = _c(84);
  const {
    contactId,
    contact
  } = t0;
  const {
    t
  } = useLocoTranslation();
  const {
    user,
    intlLocale
  } = useUser();
  const {
    limit,
    setLimit,
    pagination,
    setPagination,
    filter,
    setFilter
  } = useFilterPagination(defaultFilters, true);
  let t1;
  if ($[0] !== contactId || $[1] !== filter || $[2] !== limit || $[3] !== pagination) {
    t1 = {
      limit,
      pagination,
      contactId,
      filter
    };
    $[0] = contactId;
    $[1] = filter;
    $[2] = limit;
    $[3] = pagination;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const {
    contactAutomationTasks
  } = useContactAutomationTasks(t1);
  let t2;
  if ($[5] !== contact || $[6] !== setFilter || $[7] !== user?.timezone) {
    t2 = () => {
      if (contact) {
        setFilter({
          date: getDateRangeSinceDate(contact.createdAt, user?.timezone)
        });
      }
    };
    $[5] = contact;
    $[6] = setFilter;
    $[7] = user?.timezone;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const t3 = user?.timezone;
  let t4;
  if ($[9] !== contact || $[10] !== setFilter || $[11] !== t3) {
    t4 = [contact, setFilter, t3];
    $[9] = contact;
    $[10] = setFilter;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  useEffect(t2, t4);
  let t5;
  if ($[13] !== t) {
    t5 = t("dashboard.contact_page.automation_tasks.headers.name");
    $[13] = t;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== t) {
    t6 = t7 => {
      const {
        action,
        automationRule
      } = t7;
      return <div className="flex flex-col"><ContactAutomationTaskLink id={automationRule.id}>{t(action?.type ? actionTitleLocoKeys[action.type] : "dashboard.contact_page.automation_tasks.view_task")}</ContactAutomationTaskLink>{action && <span className="whitespace-normal">{action.data.name}</span>}</div>;
    };
    $[15] = t;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== t5 || $[18] !== t6) {
    t7 = {
      key: "automationTask",
      header: t5,
      widthPercent: 60,
      onRender: t6
    };
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== t) {
    t8 = t("dashboard.contact_page.automation_tasks.headers.date");
    $[20] = t;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== user?.dateFormat || $[23] !== user?.timeFormat || $[24] !== user?.timezone) {
    t9 = t10 => {
      const {
        automationTask
      } = t10;
      return automationTask.completedAt && <Tooltip wrapperClassName="w-full" mode="hover" label={dateTimeFormat(automationTask.completedAt, {
        timeFormat: user?.timeFormat,
        dateFormat: user?.dateFormat,
        timeZone: user?.timezone
      })}><span className="w-full truncate">{dateFormat(automationTask.completedAt, {
            dateFormat: user?.dateFormat,
            timeZone: user?.timezone
          })}</span></Tooltip>;
    };
    $[22] = user?.dateFormat;
    $[23] = user?.timeFormat;
    $[24] = user?.timezone;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== t || $[27] !== user?.dateFormat || $[28] !== user?.timeFormat || $[29] !== user?.timezone) {
    t10 = t11 => {
      const {
        automationTask: automationTask_0
      } = t11;
      return automationTask_0.completedAt && <div className="flex items-center gap-3"><span className="text-text-primary font-bold">{t("global.date")}</span><span>{dateTimeFormat(automationTask_0.completedAt, {
            timeFormat: user?.timeFormat,
            dateFormat: user?.dateFormat,
            timeZone: user?.timezone
          })}</span></div>;
    };
    $[26] = t;
    $[27] = user?.dateFormat;
    $[28] = user?.timeFormat;
    $[29] = user?.timezone;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] !== t10 || $[32] !== t8 || $[33] !== t9) {
    t11 = {
      key: "automationTask",
      header: t8,
      widthPercent: 40,
      onRender: t9,
      onRenderSmall: t10
    };
    $[31] = t10;
    $[32] = t8;
    $[33] = t9;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  let t12;
  if ($[35] !== t11 || $[36] !== t7) {
    t12 = [t7, t11];
    $[35] = t11;
    $[36] = t7;
    $[37] = t12;
  } else {
    t12 = $[37];
  }
  const columns = t12;
  let t13;
  if ($[38] !== t) {
    t13 = t("dashboard.contact_page.automation_tasks.description");
    $[38] = t;
    $[39] = t13;
  } else {
    t13 = $[39];
  }
  let t14;
  if ($[40] !== t) {
    t14 = t("dashboard.contact_page.automation_tasks.title_plural");
    $[40] = t;
    $[41] = t14;
  } else {
    t14 = $[41];
  }
  let t15;
  if ($[42] !== t14) {
    t15 = <h2 className="h-[42px] truncate text-[17px] font-bold leading-[42px]">{t14}</h2>;
    $[42] = t14;
    $[43] = t15;
  } else {
    t15 = $[43];
  }
  let t16;
  if ($[44] !== t13 || $[45] !== t15) {
    t16 = <Hint className="truncate" label={t13}>{t15}</Hint>;
    $[44] = t13;
    $[45] = t15;
    $[46] = t16;
  } else {
    t16 = $[46];
  }
  const t17 = user?.timezone;
  let t18;
  if ($[47] !== t17) {
    t18 = getToday(t17);
    $[47] = t17;
    $[48] = t18;
  } else {
    t18 = $[48];
  }
  const t19 = contact?.createdAt;
  const t20 = user?.timezone;
  let t21;
  if ($[49] !== t19 || $[50] !== t20) {
    t21 = getDate(t19, t20);
    $[49] = t19;
    $[50] = t20;
    $[51] = t21;
  } else {
    t21 = $[51];
  }
  let t22;
  if ($[52] !== contact?.createdAt || $[53] !== user?.timezone) {
    t22 = () => getDateRangeSinceDate(contact?.createdAt, user?.timezone);
    $[52] = contact?.createdAt;
    $[53] = user?.timezone;
    $[54] = t22;
  } else {
    t22 = $[54];
  }
  let t23;
  if ($[55] !== setFilter || $[56] !== setLimit || $[57] !== setPagination) {
    t23 = date => {
      setPagination({
        startFromId: undefined,
        order: OrderEnum.next
      });
      setLimit(10);
      setFilter({
        date
      });
    };
    $[55] = setFilter;
    $[56] = setLimit;
    $[57] = setPagination;
    $[58] = t23;
  } else {
    t23 = $[58];
  }
  const t24 = user?.timezone;
  const t25 = user?.createdAt;
  let t26;
  if ($[59] !== filter.date || $[60] !== intlLocale || $[61] !== t18 || $[62] !== t21 || $[63] !== t22 || $[64] !== t23 || $[65] !== t24 || $[66] !== t25) {
    t26 = <DateRangePicker maxDate={t18} minDate={t21} getDefaultDate={t22} date={filter.date} onChange={t23} timezone={t24} createdAt={t25} intlLocale={intlLocale} />;
    $[59] = filter.date;
    $[60] = intlLocale;
    $[61] = t18;
    $[62] = t21;
    $[63] = t22;
    $[64] = t23;
    $[65] = t24;
    $[66] = t25;
    $[67] = t26;
  } else {
    t26 = $[67];
  }
  let t27;
  if ($[68] !== limit || $[69] !== setLimit) {
    t27 = {
      limit,
      setLimit
    };
    $[68] = limit;
    $[69] = setLimit;
    $[70] = t27;
  } else {
    t27 = $[70];
  }
  let t28;
  if ($[71] !== pagination || $[72] !== setPagination) {
    t28 = {
      pagination,
      setPagination
    };
    $[71] = pagination;
    $[72] = setPagination;
    $[73] = t28;
  } else {
    t28 = $[73];
  }
  let t29;
  if ($[74] !== columns || $[75] !== contactAutomationTasks || $[76] !== t27 || $[77] !== t28) {
    t29 = <Table limiter={t27} paginator={t28} data={contactAutomationTasks} columns={columns} nestedId="automationTask" />;
    $[74] = columns;
    $[75] = contactAutomationTasks;
    $[76] = t27;
    $[77] = t28;
    $[78] = t29;
  } else {
    t29 = $[78];
  }
  let t30;
  if ($[79] !== contactAutomationTasks || $[80] !== t16 || $[81] !== t26 || $[82] !== t29) {
    t30 = <ListingLayout type="compact" data={contactAutomationTasks} counter={false} titleClassName="truncate" title={t16} customHeaderElement={t26}>{t29}</ListingLayout>;
    $[79] = contactAutomationTasks;
    $[80] = t16;
    $[81] = t26;
    $[82] = t29;
    $[83] = t30;
  } else {
    t30 = $[83];
  }
  return t30;
};
export default withErrorBoundary(ContactAutomationTasks);